import { type DefaultValueConverters } from '@databases/collab/default-value-converters';
import { YDatabase } from '@databases/collab/y-database';
import { UserType } from '@databases/model/value';
import { type DatabaseSdkContract } from '@databases/sdk-types';
import { type Result } from 'neverthrow';

import { tryMutate } from '../domain/result';
import { type SdkError } from '../types';

import { EntrySdk } from './entry-sdk';
import { FieldSdk } from './field-sdk';
import { MetaSdk } from './meta-sdk';
import { ValueSdk } from './value-sdk';
import { ViewSdk, type ViewSdkOptions } from './view-sdk';

const noopValueConverters: DefaultValueConverters = {
  getCurrentUser: () => ({ id: '', type: UserType.Referenced })
};

export type DatabaseSdkOptions<
  PropertiesMap extends Record<string, Record<string, unknown>> = Record<never, never>
> = ViewSdkOptions<PropertiesMap>;

/**
 * Main entry point for the Databases SDK.
 *
 * Composes FieldSdk, EntrySdk, ValueSdk, and ViewSdk. Each sub-SDK validates
 * operations through its planner before calling the corresponding YJS handler.
 *
 * @typeParam PropertiesMap - When populated via `viewLayoutRegistry`, the
 *   `views` sub-SDK gains typed property overloads.
 *
 * Usage:
 *   const sdk = DatabaseSdk.create();
 *   const result = sdk.fields.add(field);
 *
 *   if (!result.ok) console.error(result.errors);
 */
export class DatabaseSdk<
  PropertiesMap extends Record<string, Record<string, unknown>> = Record<never, never>
> implements DatabaseSdkContract<PropertiesMap> {
  readonly fields: FieldSdk;
  readonly entries: EntrySdk;
  readonly values: ValueSdk;
  readonly views: ViewSdk<PropertiesMap>;
  readonly meta: MetaSdk;

  constructor(
    private readonly ydb: YDatabase,
    options: DatabaseSdkOptions<PropertiesMap> = {}
  ) {
    this.fields = new FieldSdk(ydb);
    this.entries = new EntrySdk(ydb);
    this.values = new ValueSdk(ydb);
    this.views = new ViewSdk<PropertiesMap>(ydb, options);
    this.meta = new MetaSdk(ydb);
  }

  static create(
    converters: DefaultValueConverters = noopValueConverters,
    options: DatabaseSdkOptions = {}
  ): DatabaseSdk {
    return new DatabaseSdk(new YDatabase(undefined, converters), options);
  }

  clear(): Result<void, SdkError> {
    return tryMutate(() => {
      this.ydb.mutate((db) => {
        for (const entryId of [...db.entries]) {
          db.deleteEntry(entryId);
        }

        for (const fieldId of [...db.fields]) {
          db.deleteField(fieldId);
        }

        for (const viewId of [...db.views]) {
          db.deleteView(viewId);
        }
      });
    });
  }

  toJSON() {
    return this.ydb.toJSON();
  }
}
